import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import axios from "axios";
import Select from "react-select";
import { CSVLink } from "react-csv";
import { makeStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TablePagination from "@material-ui/core/TablePagination";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import { useReportContext } from "../ReportDataContext";
import {schoolProgramEnrollmentVsDobHeaders} from "./SchoolProgramEnrollmentVsDobTableHeaders";
import { schoolProgramEnrollmentVsDobHeaders as schoolProgramEnrollmentVsDobCsvHeaders } from "./SchoolProgramEnrollmentVsDobCSVHeaders";
import SchoolProgramEnrollmentVsDobReportRow from "./SchoolProgramEnrollmentVsDobReportRow";

function desc(a, b, orderBy) {
  if (b[orderBy] < a[orderBy]) {
    return -1;
  }
  if (b[orderBy] > a[orderBy]) {
    return 1;
  }
  return 0;
}

function stableSort(array, cmp) {
  const stabilizedThis = array.map((el, index) => [el, index]);
  stabilizedThis.sort((a, b) => {
    const order = cmp(a[0], b[0]);
    if (order !== 0) return order;
    return a[1] - b[1];
  });
  return stabilizedThis.map((el) => el[0]);
}

function getSorting(order, orderBy) {
  return order === "desc"
    ? (a, b) => desc(a, b, orderBy)
    : (a, b) => -desc(a, b, orderBy);
}

function EnhancedReportTableHead(props) {
  const { order, orderBy, onRequestSort, showActions } = props;
  const createSortHandler = (property) => (event) => {
    onRequestSort(event, property);
  };

  return (
    <TableHead>
      <TableRow>
        {schoolProgramEnrollmentVsDobHeaders.map((row, index) => (
          <TableCell
            key={`${index}"-"${row.id}`}
            align="center"
          >
            {row.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

EnhancedReportTableHead.propTypes = {
  onRequestSort: PropTypes.func.isRequired,
  order: PropTypes.string.isRequired,
  orderBy: PropTypes.string.isRequired,
  rowCount: PropTypes.number.isRequired,
};

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    marginTop: theme.spacing(3),
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  regularClassIcon: {
    color: "#9f7431",
  },
  table: {
    minWidth: 750,
  },
  tableWrapper: {
    overflowX: "auto",
  },
}));

export default function EnhancedReportTable({ reports, showActions}) {
  const classes = useStyles();
  const branch = useReportContext().AUTH.branch;
  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [accordianReset, setAccordianReset] = useState(false);
  const [schoolFilter, setSchoolFilter] = useState(""); // school_information_id
  const [schoolOptions, setSchoolOptions] = useState([]);

  // School dropdown options mirror the School Programs page source.
  useEffect(() => {
    let cancelled = false;
    if (branch && branch.branch_id) {
      axios(`api/school-programs/schools/${branch.branch_id}`)
        .then((res) => {
          if (cancelled) return;
          setSchoolOptions(
            (res.data.results || []).map((s) => ({
              value: s.id,
              label: s.value,
            }))
          );
        })
        .catch((e) => console.log("School filter options error: ", e));
    }
    return () => {
      cancelled = true;
    };
  }, [branch]);

  const rows = useMemo(
    () =>
      (reports || []).filter(
        (r) =>
          !schoolFilter ||
          String(r.school_information_id) === String(schoolFilter)
      ),
    [reports, schoolFilter]
  );

  // Keep pagination in range when the filter shrinks the result set.
  useEffect(() => {
    setPage(0);
  }, [schoolFilter]);

  function handleRequestSort(event, property) {
    const isDesc = orderBy === property && order === "desc";
    setOrder(isDesc ? "asc" : "desc");
    setOrderBy(property);
  }

  function handleChangePage(event, newPage) {
    setPage(newPage);
    setAccordianReset(true);
  }

  function handleChangeRowsPerPage(event) {
    setRowsPerPage(+event.target.value);
  }

  const emptyRows =
    rowsPerPage - Math.min(rowsPerPage, rows?.length - page * rowsPerPage);

  return (
    <div className={classes.root}>
      <div className="form-group row align-items-end mb-3">
        <div className="col-lg-3">
          <Select
            isClearable
            placeholder="Select School"
            options={schoolOptions}
            value={schoolOptions.find((o) => o.value === schoolFilter) || null}
            onChange={(selected) =>
              setSchoolFilter(selected ? selected.value : "")
            }
          />
          <small className="form-text text-muted">
            <b>Filter</b> by School
          </small>
        </div>
        <div className="col-lg-9 text-right">
          {rows.length > 0 && (
            <button type="button" className="btn btn-light">
              <CSVLink
                filename={`${branch.branch_name} - Enrollment Vs DOB - School Program - Report.csv`}
                data={rows}
                headers={schoolProgramEnrollmentVsDobCsvHeaders}
              >
                Download Report
              </CSVLink>
            </button>
          )}
        </div>
      </div>
      <Paper className={classes.paper}>
        <div className={classes.tableWrapper}>
          <Table className={classes.table} aria-labelledby="tableTitle">
            <EnhancedReportTableHead
              order={order}
              orderBy={orderBy}
              onRequestSort={handleRequestSort}
              rowCount={rows ? rows.length : 0}
              showActions={showActions}
            />
              <TableBody>
                {rows &&
                  stableSort(rows, getSorting(order, orderBy))
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((row, index) => {
                      return (
                        <SchoolProgramEnrollmentVsDobReportRow
                          key={`${row.student_id}-${row.school_program_id}-${index}`}
                          row={row}
                          reset={accordianReset}
                          setReset={setAccordianReset}
                          showActions={showActions}
                        />
                      );
                    })}
                {emptyRows > 0 && (
                  <TableRow style={{ height: 49 * emptyRows }}>
                    <TableCell colSpan={7} />
                  </TableRow>
                )}
              </TableBody>
          </Table>
        </div>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={rows ? rows.length : 0}
          rowsPerPage={rowsPerPage}
          page={page}
          backIconButtonProps={{
            "aria-label": "Previous Page",
          }}
          nextIconButtonProps={{
            "aria-label": "Next Page",
          }}
          onChangePage={handleChangePage}
          onChangeRowsPerPage={handleChangeRowsPerPage}
        />
      </Paper>
    </div>
  );
}
